// src/pages/PartNumberCalculator.tsx
//
// Picks a free part number and mold/insert for a new part.
//
// Part numbers follow MarineEast's catalog: each subcategory owns a block of
// numbers (FPT to MPT Adaptor is 8725-8749), so the calculator reads the
// categories from the customer site's database and offers numbers that sit
// with similar parts. "Free" means not in Catalyst, QuickBooks, or MarineEast.
//
// Nothing is saved here. "Use these" opens the New Product form with the
// numbers filled in; the part is created when that form is submitted.
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Mold } from "../types/Product";

interface Subcategory { id: number; name: string; productCount: number }
interface Category { id: number; name: string; subcategories: Subcategory[] }

interface Neighbor { number: number; partNumber: string; name: string; source: string; subcategory: string | null }
interface Suggestion { number: number; below: Neighbor | null; above: Neighbor | null }
interface Block {
  min: number; max: number; step: number; count: number;
  nextInSequence: Suggestion[]; skipped: Neighbor[]; stoppedBy: Neighbor | null;
  gaps: Suggestion[]; gapTotal: number;
  offPatternGaps: Suggestion[]; offPatternTotal: number;
}
interface PartNumberResult { series: string; label: string; productCount: number; blocks: Block[]; note: string | null }

interface TakenPart { partNumber: string; name: string; source: string; subcategory: string | null }
interface NearResult {
  partNumber: string; series: string; base: number | null; exact: boolean; matches: TakenPart[];
  subcategoryId: number | null; subcategory: string | null; above: Suggestion[]; below: Suggestion[]; note: string | null;
}
interface CheckResult { partNumber: string; base: number | null; exact: TakenPart[]; sameBase: TakenPart[]; free: boolean }

interface NextInsert {
  moldId: number; baseNumber: string; nextInsert: string; highestInsert: string | null;
  insertCount: number; inserts: string[]; qbOnly: string[]; ignoredCodes: string[];
}
interface NextBase {
  prefix: string; nextBase: string; highestBase: string | null; moldCount: number;
  qbOnly: string[]; malformed: { moldID: number; baseNumber: string }[];
}

type MoldMode = "existing" | "new";

const PLA = "PLA";

export default function PartNumberCalculator() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ---- part number ----
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [catError, setCatError] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string>("");
  const [subcategoryId, setSubcategoryId] = useState<string>("");
  const [result, setResult] = useState<PartNumberResult | null>(null);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [partNumber, setPartNumber] = useState("");
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [relativeQuery, setRelativeQuery] = useState("");
  const [relative, setRelative] = useState<NearResult | null>(null);
  const [findingRelative, setFindingRelative] = useState(false);

  // ---- mold ----
  const [molds, setMolds] = useState<Mold[]>([]);
  const [moldMode, setMoldMode] = useState<MoldMode>("existing");
  const [moldSearch, setMoldSearch] = useState("");
  const [moldId, setMoldId] = useState<string>("");
  const [nextInsert, setNextInsert] = useState<NextInsert | null>(null);
  const [prefix, setPrefix] = useState("102");
  const [nextBase, setNextBase] = useState<NextBase | null>(null);
  const [insertCode, setInsertCode] = useState("");
  const [colorCode, setColorCode] = useState("01");
  const [creatingMold, setCreatingMold] = useState(false);

  useEffect(() => {
    axios.get<Category[]>("/api/part-numbering/categories", { timeout: 30000 })
      .then((r) => setCategories(r.data))
      .catch((err) => setCatError(err?.response?.data?.error || err?.message || "Could not load MarineEast categories."));
    axios.get<Mold[]>("/api/molds").then((r) => setMolds(r.data)).catch(() => toast.error("Could not load molds"));
  }, []);

  const category = useMemo(() => categories?.find((c) => c.id.toString() === categoryId), [categories, categoryId]);

  // Fetch suggestions whenever a subcategory (or PLA) is picked.
  useEffect(() => {
    if (!subcategoryId) { setResult(null); return; }
    setLoadingNumbers(true);
    setResult(null);
    const url = subcategoryId === PLA
      ? "/api/part-numbering/part-numbers/pla"
      : `/api/part-numbering/part-numbers?subcategoryId=${subcategoryId}`;
    axios.get<PartNumberResult>(url, { timeout: 60000 })
      .then((r) => { setResult(r.data); setLoadingNumbers(false); })
      .catch((err) => {
        toast.error(err?.response?.data?.error || err?.message || "Could not load part numbers");
        setLoadingNumbers(false);
      });
  }, [subcategoryId]);

  const pickNumber = (n: number) => {
    setPartNumber(subcategoryId === PLA ? `PLA${n}` : String(n));
    setCheck(null);
  };

  // "It's like the 8737 but bigger": find that part anywhere, show the free
  // numbers either side of it, and if the site has it, jump to its block.
  const findRelative = async () => {
    const q = relativeQuery.trim();
    if (!q) return;
    setFindingRelative(true);
    setRelative(null);
    try {
      const r = await axios.get<NearResult>("/api/part-numbering/near", { params: { partNumber: q }, timeout: 60000 });
      setRelative(r.data);
      if (r.data.subcategoryId != null && categories) {
        const parent = categories.find((c) => c.subcategories.some((s) => s.id === r.data.subcategoryId));
        if (parent) {
          setCategoryId(parent.id.toString());
          setSubcategoryId(r.data.subcategoryId.toString());
        }
      } else if (r.data.series === "PLA") {
        setCategoryId(PLA);
        setSubcategoryId(PLA);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) toast.error(`No part "${q}" in Catalyst, QuickBooks or MarineEast`);
      else toast.error(err?.response?.data?.error || err?.message || "Lookup failed");
    } finally {
      setFindingRelative(false);
    }
  };

  const runCheck = async () => {
    if (!partNumber.trim()) return;
    setChecking(true);
    try {
      const r = await axios.get<CheckResult>("/api/part-numbering/check", { params: { partNumber: partNumber.trim() }, timeout: 60000 });
      setCheck(r.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  // ---- mold lookups ----
  useEffect(() => {
    if (moldMode !== "existing" || !moldId) { setNextInsert(null); return; }
    axios.get<NextInsert>(`/api/part-numbering/molds/${moldId}/next-insert`)
      .then((r) => { setNextInsert(r.data); setInsertCode(r.data.nextInsert); })
      .catch((err) => toast.error(err?.response?.data?.error || err?.message || "Could not work out the next insert"));
  }, [moldMode, moldId]);

  useEffect(() => {
    if (moldMode !== "new") { setNextBase(null); return; }
    if (!/^\d{3}$/.test(prefix)) { setNextBase(null); return; }
    axios.get<NextBase>("/api/part-numbering/molds/next-base", { params: { prefix } })
      .then((r) => { setNextBase(r.data); setInsertCode("A"); })
      .catch((err) => toast.error(err?.response?.data?.error || err?.message || "Could not work out the next mold number"));
  }, [moldMode, prefix]);

  const selectedMold = molds.find((m) => m.moldID.toString() === moldId);
  const moldMatches = useMemo(
    () => molds.filter((m) => m.baseNumber.toLowerCase().includes(moldSearch.toLowerCase())).slice(0, 8),
    [molds, moldSearch]
  );

  const moldLabel = moldMode === "existing" ? selectedMold?.baseNumber : nextBase?.nextBase;
  const ready = partNumber.trim() && moldLabel && insertCode.trim();

  // Hands the numbers to the New Product form. A brand-new mold has to exist
  // first so the form's mold picker can reference it.
  const useThese = async () => {
    if (!ready) return;
    let targetMoldId = moldId;
    if (moldMode === "new") {
      if (!nextBase) return;
      setCreatingMold(true);
      try {
        const r = await axios.post<Mold>("/api/molds", {
          baseNumber: nextBase.nextBase,
          materialCompatibility: "",
          maintenanceSchedule: new Date().toISOString(),
        });
        targetMoldId = r.data.moldID.toString();
        toast.success(`Mold ${nextBase.nextBase} created`);
      } catch (err: any) {
        toast.error(err?.response?.data?.error || err?.message || "Could not create the mold");
        setCreatingMold(false);
        return;
      }
      setCreatingMold(false);
    }
    const params = new URLSearchParams({
      partNumber: partNumber.trim(),
      moldId: targetMoldId,
      insertCode: insertCode.trim().toUpperCase(),
      colorCode: colorCode.trim(),
    });
    navigate(`/products/new?${params.toString()}`);
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20, color: "#ddd" }}>
      <button onClick={() => navigate(-1)} style={btn("#0f0")}>Back</button>
      <h2 style={{ color: "#0f0", margin: "16px 0 4px" }}>Part Number Calculator</h2>
      <p style={{ color: "#888", margin: "0 0 18px", fontSize: "0.85rem" }}>
        Pick where the part belongs on the MarineEast site and which mold makes it. Nothing is saved until you submit the New Product form.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
        {/* ---------------- Part number ---------------- */}
        <section style={panel}>
          <h3 style={panelTitle}>1. Part number</h3>

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
            <label style={{ ...field, flex: 1, minWidth: 180 }}>
              Based on an existing part?
              <input
                value={relativeQuery}
                onChange={(e) => setRelativeQuery(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") findRelative(); }}
                placeholder="e.g. 8737"
                style={inputStyle}
              />
            </label>
            <button onClick={findRelative} disabled={!relativeQuery.trim() || findingRelative} style={btn("#0ff")}>
              {findingRelative ? "Looking…" : "Find it"}
            </button>
          </div>

          {relative && (
            <div style={{ ...banner("#0ff"), marginTop: 0, marginBottom: 14 }}>
              <div style={{ color: "#fff" }}>
                <b>{relative.matches[0].partNumber}</b>
                {relative.matches[0].name ? ` — ${relative.matches[0].name}` : ""}
                {!relative.exact && <span style={{ color: "#fc6" }}> (no exact "{relative.partNumber}"; matched by base number)</span>}
              </div>
              <div style={{ color: "#aaa", marginTop: 2 }}>
                Found in: {Array.from(new Set(relative.matches.map((m) => m.source))).join(", ")}
                {relative.subcategory ? <> · on the site under <b style={{ color: "#fff" }}>{relative.subcategory}</b> (block shown below)</> : " · not on the MarineEast site"}
              </div>
              {relative.note && <div style={{ color: "#fc6", marginTop: 6 }}>{relative.note}</div>}
              {(relative.below.length > 0 || relative.above.length > 0) && (
                <div style={{ marginTop: 10 }}>
                  <NearRow label="Nearest free below" items={[...relative.below].reverse()} series={relative.series} ownSubcategory={relative.subcategory} chosen={partNumber} onPick={(n) => { setPartNumber(relative.series === "PLA" ? `PLA${n}` : String(n)); setCheck(null); }} />
                  <NearRow label="Nearest free above" items={relative.above} series={relative.series} ownSubcategory={relative.subcategory} chosen={partNumber} onPick={(n) => { setPartNumber(relative.series === "PLA" ? `PLA${n}` : String(n)); setCheck(null); }} />
                </div>
              )}
            </div>
          )}

          {catError && <div style={banner("#f66")}>{catError}</div>}
          {!categories && !catError && <div style={{ color: "#888" }}>Loading MarineEast categories…</div>}
          {categories && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <label style={{ ...field, flex: 1, minWidth: 180 }}>
                Category
                <select
                  value={categoryId}
                  onChange={(e) => { setCategoryId(e.target.value); setSubcategoryId(e.target.value === PLA ? PLA : ""); }}
                  style={inputStyle}
                >
                  <option value="">— choose —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  <option value={PLA}>PLA (bought molds)</option>
                </select>
              </label>
              {categoryId && categoryId !== PLA && (
                <label style={{ ...field, flex: 1, minWidth: 180 }}>
                  Subcategory
                  <select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)} style={inputStyle}>
                    <option value="">— choose —</option>
                    {category?.subcategories.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.productCount})</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {loadingNumbers && <div style={{ color: "#888", marginTop: 12 }}>Working out free numbers…</div>}

          {result && (
            <div style={{ marginTop: 14 }}>
              <div style={{ color: "#aaa", fontSize: "0.85rem" }}>
                <b style={{ color: "#fff" }}>{result.label}</b> — {result.productCount} products on the site
              </div>
              {result.note && <div style={banner("#fc6")}>{result.note}</div>}
              {result.blocks.map((b) => (
                <BlockView key={b.min} block={b} series={result.series} chosen={partNumber} onPick={pickNumber} />
              ))}
            </div>
          )}

          <div style={{ marginTop: 18, display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <label style={{ ...field, flex: 1, minWidth: 160 }}>
              Part number to use
              <input
                value={partNumber}
                onChange={(e) => { setPartNumber(e.target.value.toUpperCase()); setCheck(null); }}
                placeholder="Pick one above or type one"
                style={inputStyle}
              />
            </label>
            <button onClick={runCheck} disabled={!partNumber.trim() || checking} style={btn("#0ff")}>
              {checking ? "Checking…" : "Check it's free"}
            </button>
          </div>
          <div style={{ color: "#777", fontSize: "0.78rem", marginTop: 6 }}>
            Add a suffix for variants (W, G, -L, -R). The base number is what has to be free.
          </div>
          {check && (
            check.free
              ? <div style={banner("#0f0")}>{check.partNumber} is free everywhere — Catalyst, QuickBooks and MarineEast.</div>
              : (
                <div style={banner("#f66")}>
                  {check.exact.length > 0 && (
                    <div><b>{check.partNumber} already exists:</b> {check.exact.map((t) => `${t.source}${t.name ? ` (${t.name})` : ""}`).join(", ")}</div>
                  )}
                  {check.sameBase.length > 0 && (
                    <div style={{ marginTop: check.exact.length ? 6 : 0 }}>
                      <b>Same base number in use:</b>{" "}
                      {check.sameBase.slice(0, 8).map((t) => `${t.partNumber} (${t.source}${t.name ? `, ${t.name}` : ""})`).join("; ")}
                      {check.sameBase.length > 8 && ` … and ${check.sameBase.length - 8} more`}
                    </div>
                  )}
                </div>
              )
          )}
        </section>

        {/* ---------------- Mold ---------------- */}
        <section style={panel}>
          <h3 style={panelTitle}>2. Mold &amp; insert</h3>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
            <label style={radio}>
              <input type="radio" checked={moldMode === "existing"} onChange={() => setMoldMode("existing")} /> New cavity in an existing mold
            </label>
            <label style={radio}>
              <input type="radio" checked={moldMode === "new"} onChange={() => setMoldMode("new")} /> Brand new mold
            </label>
          </div>

          {moldMode === "existing" && (
            <>
              <label style={field}>
                Mold
                <input
                  value={moldSearch}
                  onChange={(e) => { setMoldSearch(e.target.value); setMoldId(""); setNextInsert(null); }}
                  placeholder="Type a mold number…"
                  style={inputStyle}
                />
              </label>
              {moldSearch && !moldId && (
                <div style={{ border: "1px solid #0f0", borderRadius: 6, background: "#222", marginTop: 4 }}>
                  {moldMatches.length === 0 && <div style={{ ...item, color: "#888", cursor: "default" }}>No molds found</div>}
                  {moldMatches.map((m) => (
                    <div key={m.moldID} style={item} onClick={() => { setMoldId(m.moldID.toString()); setMoldSearch(m.baseNumber); }}>
                      {m.baseNumber}
                    </div>
                  ))}
                </div>
              )}
              {nextInsert && (
                <div style={banner("#0f0")}>
                  <div style={{ fontSize: "1.1rem" }}>
                    Next insert: <b>{nextInsert.nextInsert}</b>
                    <span style={{ color: "#aaa", fontSize: "0.85rem" }}>
                      {" "}→ {nextInsert.baseNumber}{nextInsert.nextInsert}{colorCode}
                    </span>
                  </div>
                  <div style={{ color: "#aaa", marginTop: 4 }}>
                    {nextInsert.insertCount === 0
                      ? "No inserts recorded for this mold yet."
                      : <>{nextInsert.insertCount} inserts in use, highest <b style={{ color: "#fff" }}>{nextInsert.highestInsert}</b>. Gaps are skipped — a missing letter may be assigned on paper.</>}
                  </div>
                  {nextInsert.qbOnly.length > 0 && (
                    <div style={{ color: "#fc6", marginTop: 4 }}>
                      Only in QuickBooks, not Catalyst: {nextInsert.qbOnly.join(", ")}
                    </div>
                  )}
                  {nextInsert.ignoredCodes.length > 0 && (
                    <div style={{ color: "#f96", marginTop: 4 }}>
                      Ignored (not a letter code): {nextInsert.ignoredCodes.map((c) => `"${c}"`).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {moldMode === "new" && (
            <>
              <label style={{ ...field, maxWidth: 220 }}>
                Customer series
                <select value={["102", "103", "104"].includes(prefix) ? prefix : "other"} onChange={(e) => setPrefix(e.target.value === "other" ? "" : e.target.value)} style={inputStyle}>
                  <option value="102">102 — MarineEast</option>
                  <option value="103">103</option>
                  <option value="104">104</option>
                  <option value="other">Other…</option>
                </select>
              </label>
              {!["102", "103", "104"].includes(prefix) && (
                <label style={{ ...field, maxWidth: 220, marginTop: 8 }}>
                  Series (three digits)
                  <input value={prefix} onChange={(e) => setPrefix(e.target.value.replace(/\D/g, "").slice(0, 3))} style={inputStyle} />
                </label>
              )}
              {nextBase && (
                <div style={banner("#0f0")}>
                  <div style={{ fontSize: "1.1rem" }}>
                    Next mold: <b>{nextBase.nextBase}</b>
                    <span style={{ color: "#aaa", fontSize: "0.85rem" }}> → {nextBase.nextBase}A{colorCode}</span>
                  </div>
                  <div style={{ color: "#aaa", marginTop: 4 }}>
                    {nextBase.highestBase
                      ? <>{nextBase.moldCount} molds in the {nextBase.prefix} series, highest <b style={{ color: "#fff" }}>{nextBase.highestBase}</b>. Checked against Catalyst and QuickBooks.</>
                      : <>No molds in the {nextBase.prefix} series yet.</>}
                  </div>
                  {nextBase.qbOnly.length > 0 && (
                    <div style={{ color: "#fc6", marginTop: 4 }}>
                      {nextBase.qbOnly.length} mold{nextBase.qbOnly.length === 1 ? "" : "s"} in this series exist only in QuickBooks, not Catalyst
                      {nextBase.qbOnly.length <= 6 ? `: ${nextBase.qbOnly.join(", ")}` : ` (highest ${nextBase.qbOnly[nextBase.qbOnly.length - 1]})`}.
                    </div>
                  )}
                  {nextBase.malformed.length > 0 && (
                    <div style={{ color: "#f96", marginTop: 4 }}>
                      Not counted — fix these mold numbers by hand:{" "}
                      {nextBase.malformed.map((m, i) => (
                        <React.Fragment key={m.moldID}>
                          {i > 0 && ", "}
                          <a href={`/molds/${m.moldID}`} onClick={(e) => { e.preventDefault(); navigate(`/molds/${m.moldID}`); }} style={{ color: "#f96" }}>
                            {m.baseNumber || "(blank)"}
                          </a>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
            <label style={{ ...field, flex: 1 }}>
              Insert code
              <input value={insertCode} onChange={(e) => setInsertCode(e.target.value.toUpperCase())} style={inputStyle} />
            </label>
            <label style={{ ...field, flex: 1 }}>
              Color code
              <input value={colorCode} onChange={(e) => setColorCode(e.target.value)} style={inputStyle} />
            </label>
          </div>
        </section>
      </div>

      {/* ---------------- Hand-off ---------------- */}
      <div style={{ ...panel, marginTop: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ color: "#888", fontSize: "0.8rem" }}>Ready to create</div>
          <div style={{ fontSize: "1.05rem", color: "#fff" }}>
            Part <b style={{ color: ready ? "#0f0" : "#666" }}>{partNumber || "—"}</b>
            {" · "}Mold <b style={{ color: ready ? "#0f0" : "#666" }}>{moldLabel || "—"}</b>
            {" · "}Insert <b style={{ color: ready ? "#0f0" : "#666" }}>{insertCode || "—"}</b>
            {colorCode && <span style={{ color: "#aaa" }}> color {colorCode}</span>}
          </div>
          {moldMode === "new" && nextBase && (
            <div style={{ color: "#fc6", fontSize: "0.8rem", marginTop: 4 }}>Mold {nextBase.nextBase} will be created when you continue.</div>
          )}
        </div>
        <button onClick={useThese} disabled={!ready || creatingMold} style={{ ...btn("#0f0"), opacity: ready ? 1 : 0.4, fontWeight: 600 }}>
          {creatingMold ? "Creating mold…" : "Use these → New Product"}
        </button>
      </div>
    </div>
  );
}

function BlockView({ block, series, chosen, onPick }: { block: Block; series: string; chosen: string; onPick: (n: number) => void }) {
  const fmt = (n: number) => (series === "PLA" ? `PLA${n}` : String(n));
  const isChosen = (n: number) => chosen === fmt(n);
  const chip = (s: Suggestion, muted = false) => (
    <button
      key={s.number}
      onClick={() => onPick(s.number)}
      title={neighborTitle(s, fmt)}
      style={{
        ...btn(isChosen(s.number) ? "#fff" : muted ? "#999" : "#0f0"),
        background: isChosen(s.number) ? "#0f0" : "transparent",
        color: isChosen(s.number) ? "#000" : muted ? "#999" : "#0f0",
        padding: "6px 12px",
      }}
    >
      {fmt(s.number)}
    </button>
  );

  return (
    <div style={{ marginTop: 12, padding: 12, border: "1px solid #333", borderRadius: 8 }}>
      <div style={{ color: "#fff", marginBottom: 8 }}>
        Block <b>{fmt(block.min)}–{fmt(block.max)}</b>
        <span style={{ color: "#888", fontSize: "0.82rem" }}> · {block.count} numbers · counts by {block.step}</span>
      </div>

      {block.nextInSequence.length > 0 && (
        <Row label="Next in sequence">
          {block.nextInSequence.map((s) => chip(s))}
        </Row>
      )}
      {block.skipped.length > 0 && (
        <div style={{ color: "#888", fontSize: "0.78rem", margin: "4px 0 8px" }}>
          Already used past the block: {block.skipped.slice(0, 6).map((n) => `${n.partNumber} (${n.source})`).join(", ")}
          {block.skipped.length > 6 && ` … and ${block.skipped.length - 6} more`}.
        </div>
      )}
      {block.stoppedBy && (
        <div style={{ color: "#888", fontSize: "0.78rem", margin: "4px 0 8px" }}>
          {block.nextInSequence.length === 0 ? "Can't extend: " : "Stops at "}
          {block.stoppedBy.partNumber} is {block.stoppedBy.subcategory ? `in ${block.stoppedBy.subcategory}` : "on the site in another category"}
          {block.stoppedBy.name ? ` (${block.stoppedBy.name})` : ""}.
        </div>
      )}
      {(block.gaps.length > 0 || block.gapTotal > 0) && (
        <Row label={`Gaps inside the block${block.gapTotal > block.gaps.length ? ` (first ${block.gaps.length} of ${block.gapTotal})` : ""}`}>
          {block.gaps.map((s) => chip(s))}
        </Row>
      )}
      {block.offPatternTotal > 0 && (
        <Row label={`Off pattern — between the usual steps${block.offPatternTotal > block.offPatternGaps.length ? ` (first ${block.offPatternGaps.length} of ${block.offPatternTotal})` : ""}`}>
          {block.offPatternGaps.map((s) => chip(s, true))}
        </Row>
      )}
      {block.nextInSequence.length === 0 && block.gapTotal === 0 && block.offPatternTotal === 0 && (
        <div style={{ color: "#f96", fontSize: "0.82rem" }}>This block is full.</div>
      )}
      <div style={{ color: "#666", fontSize: "0.75rem", marginTop: 6 }}>Hover a number to see its neighbours.</div>
    </div>
  );
}

// Free numbers near a relative often sit inside a neighbouring block, so each
// chip says whose territory it is in when that differs from the relative's.
function NearRow({ label, items, series, ownSubcategory, chosen, onPick }: {
  label: string; items: Suggestion[]; series: string; ownSubcategory: string | null; chosen: string; onPick: (n: number) => void;
}) {
  if (items.length === 0) return null;
  const fmt = (n: number) => (series === "PLA" ? `PLA${n}` : String(n));
  const territory = (s: Suggestion) => {
    const b = s.below?.subcategory;
    const a = s.above?.subcategory;
    const t = b && a && b === a ? b : b || a;
    return t && t !== ownSubcategory ? t : null;
  };
  return (
    <Row label={label}>
      {items.map((s) => {
        const on = chosen === fmt(s.number);
        const t = territory(s);
        return (
          <button
            key={s.number}
            onClick={() => onPick(s.number)}
            title={neighborTitle(s, fmt)}
            style={{ ...btn(on ? "#fff" : t ? "#9c9" : "#0f0"), background: on ? "#0f0" : "transparent", color: on ? "#000" : t ? "#9c9" : "#0f0", padding: "6px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
          >
            <span>{fmt(s.number)}</span>
            {t && <span style={{ fontSize: "0.65rem", color: on ? "#030" : "#888" }}>{t}</span>}
          </button>
        );
      })}
    </Row>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ color: "#aaa", fontSize: "0.78rem", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{children}</div>
    </div>
  );
}

function neighborTitle(s: Suggestion, fmt: (n: number) => string) {
  const side = (n: Neighbor | null) =>
    n ? `${n.partNumber}${n.name ? ` — ${n.name}` : ""}${n.subcategory ? ` [${n.subcategory}]` : ` [${n.source}]`}` : "nothing";
  return `${fmt(s.number)}\nBelow: ${side(s.below)}\nAbove: ${side(s.above)}`;
}

const panel: React.CSSProperties = { padding: 16, border: "1px solid #333", borderRadius: 10, background: "#161616" };
const panelTitle: React.CSSProperties = { color: "#0f0", margin: "0 0 12px", fontSize: "1rem" };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", color: "#aaa", fontSize: "0.82rem" };
const radio: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, color: "#ddd", cursor: "pointer" };
const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  background: "#222",
  border: "1px solid #444",
  borderRadius: 6,
  color: "#fff",
  fontSize: "14px",
  marginTop: 4,
};
const item: React.CSSProperties = { padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #333", color: "#fff" };

const btn = (c: string): React.CSSProperties => ({
  background: "transparent",
  color: c,
  border: `1px solid ${c}`,
  borderRadius: 8,
  padding: "9px 18px",
  fontSize: "0.88rem",
  cursor: "pointer",
});

const banner = (c: string): React.CSSProperties => ({
  marginTop: 12,
  padding: "12px 16px",
  border: `1px solid ${c}`,
  borderLeft: `3px solid ${c}`,
  borderRadius: 8,
  background: "#1a1a1a",
  color: c,
  fontSize: "0.85rem",
});
